import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectCanManage, selectPendingRockSetup } from '@/features/auth/authSlice'
import { isFramed } from '@/utils/framing'
import { isUuid } from '@/utils/uuid'
import { rockSetupPath } from './setupPath'
import { markRockSetupVisited, wasRockSetupVisited } from './setupSession'

/** Any guided setup page (any connection): never force a redirect from inside one. */
const SETUP_PATH_PREFIX = '/setup/rock/'

/**
 * Brief §6 decision 4: an owner or administrator whose linked Rock hasn't
 * finished guided setup (`/me.pending_rock_setup`) is sent into
 * `/setup/rock/<id>` on the first app page they load in a browser session.
 * Once only: the visit is remembered for the session (`setupSession.js`),
 * and opening the setup any other way counts too, so "Save and exit" is
 * never fought. Runs in the app shell only (so never in an embed or on the
 * public/auth pages), never inside a frame, and never while the person is
 * already inside `/setup/rock/*` (for any connection).
 *
 * The path is built from the connection id, never taken from the payload's
 * `setup_path`, so a malformed payload can't send anyone off-app.
 *
 * @param {Object} [options]
 * @param {boolean} [options.isBlocked=false] - another shell redirect owns this load (the trial paywall)
 * @returns {void}
 */
export function usePendingRockSetupRedirect({ isBlocked = false } = {}) {
  const pending = useSelector(selectPendingRockSetup)
  const canManage = useSelector(selectCanManage)
  const navigate = useNavigate()
  const location = useLocation()
  const connectionId = pending?.connection_id
  const from = `${location.pathname}${location.search}`

  useEffect(() => {
    if (isBlocked || !canManage || !isUuid(connectionId)) return
    if (isFramed() || wasRockSetupVisited(connectionId)) return
    if (location.pathname.startsWith(SETUP_PATH_PREFIX)) return
    markRockSetupVisited(connectionId)
    navigate(rockSetupPath(connectionId), { replace: true, state: { from } })
  }, [isBlocked, canManage, connectionId, from, navigate, location.pathname])
}

export default usePendingRockSetupRedirect
